import { useCallback } from 'react';
import { buildApiUrl } from '@/lib/globals';
import type { AuthorizationResponse, BotQuiz, BotQuizAnswer, BotQuizAttempt, BotQuizCaptchaAnswers } from '@/lib/types';
import { useWalletContext } from '@/context/WalletContext';

const quizQuestionUrl = buildApiUrl('quiz-questions');
const submitQuizUrl = buildApiUrl('submit-quiz');

type FetchQuizResponse = BotQuizAttempt[];
type SubmitQuizRequest = {
    auth: string;
    submission: BotQuizAnswer[];
    captcha: BotQuizCaptchaAnswers;
};

const EMPTY_SUBMIT_REQUEST: SubmitQuizRequest = {
    auth: '',
    submission: [],
    captcha: {
        id: '',
        answers: [],
    },
};

interface UseQuizQuestions {
    fetchQuiz: () => Promise<FetchQuizResponse>;
    submitQuiz: (request: string | SubmitQuizRequest) => Promise<boolean>;
}

export const useQuizQuestions = (address?: string): UseQuizQuestions => {
    const { setSignedAddress, setSignature, setAuthCode } = useWalletContext();

    const submitQuizQuestionsAsync = async (request: string | SubmitQuizRequest): Promise<boolean> => {
        if (!address) {
            const error = 'No wallet address found';
            console.warn(error);
            return Promise.reject(error);
        }
        try {
            const response = await fetch(submitQuizUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
                body: JSON.stringify({
                    walletAddress: address,
                    code: typeof request === 'string' ? request : undefined,
                    ...(typeof request === 'object' ? request : EMPTY_SUBMIT_REQUEST),
                }),
            });
            if (!response.ok) {
                return false;
            }

            const { signature }: AuthorizationResponse = await response.json();
            if (Array.isArray(signature)) {
                setSignature(signature);
                setSignedAddress(address);
                return true;
            } else if (typeof signature === 'string') {
                setAuthCode(signature);
                return true;
            } else {
                return false;
            }
        } catch (e) {
            if (process.env.NODE_ENV === 'development') {
                console.warn(
                    'For local development, you must have onlybots-functions running to get a recaptcha score.'
                );
            }
            console.error(e);
            return Promise.reject(e);
        }
    };

    const fetchQuizQuestionsAsync = async (): Promise<FetchQuizResponse> => {
        try {
            const response = await fetch(quizQuestionUrl + '/' + encodeURIComponent(address ?? ''), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
            });
            const {
                quiz: { attempts },
            }: { quiz: BotQuiz } = await response.json();
            return attempts;
        } catch (e) {
            if (process.env.NODE_ENV === 'development') {
                console.warn(
                    'For local development, you must have onlybots-functions running to get a recaptcha score.'
                );
            }
            console.error(e);
            return Promise.reject(e);
        }
    };

    const fetchQuiz = useCallback(fetchQuizQuestionsAsync, [address]);
    const submitQuiz = useCallback(submitQuizQuestionsAsync, [address, setSignature, setSignedAddress, setAuthCode]);

    return {
        fetchQuiz,
        submitQuiz,
    };
};
